// src/pages/admin/ApiMonitor.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { checkServiceStatus } from '../../api/verificationServices';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const ApiMonitor = () => {
  const [apiStatuses, setApiStatuses] = useState({
    location: { primary: { status: 'checking' }, fallback: { status: 'checking' } },
    email: { primary: { status: 'checking' }, fallback: { status: 'checking' } },
    phone: { primary: { status: 'checking' }, fallback: { status: 'checking' } }
  });

  const [rateLimits, setRateLimits] = useState({
    location: { count: 0, resetTime: null },
    email: { count: 0, resetTime: null },
    phone: { count: 0, resetTime: null }
  });

  const checkApis = async () => {
    const services = ['location', 'email', 'phone'];
    
    for (const service of services) {
      try {
        const result = await checkServiceStatus(service);
        const timestamp = new Date().toISOString();
        
        setApiStatuses(prev => ({
          ...prev,
          [service]: {
            ...result,
            lastCheck: timestamp
          }
        }));
      } catch (error) {
        console.error(`Error checking ${service} service:`, error);
      }
    }
  };

  useEffect(() => {
    // Initial check
    checkApis();

    // Set up interval for periodic checks (every 5 minutes)
    const interval = setInterval(checkApis, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-status-success-border';
      case 'offline':
        return 'bg-status-error-border';
      case 'degraded':
        return 'bg-status-warning-border';
      default:
        return 'bg-text-tertiary';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const ServiceCard = ({ title, service, primaryService, fallbackService }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-text-secondary">Primary Service ({primaryService}):</p>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(apiStatuses[service].primary.status)}`} />
              <p className="font-medium text-text-primary">{apiStatuses[service].primary.status}</p>
            </div>
            {apiStatuses[service].primary.error && (
              <p className="text-sm text-status-error-text mt-1">{apiStatuses[service].primary.error}</p>
            )}
          </div>

          <div>
            <p className="text-sm text-text-secondary">Fallback Service ({fallbackService}):</p>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(apiStatuses[service].fallback.status)}`} />
              <p className="font-medium text-text-primary">{apiStatuses[service].fallback.status}</p>
            </div>
            {apiStatuses[service].fallback.error && (
              <p className="text-sm text-status-error-text mt-1">{apiStatuses[service].fallback.error}</p>
            )}
          </div>

          <div>
            <p className="text-sm text-text-secondary">Last Checked:</p>
            <p className="font-medium text-text-primary">{formatTime(apiStatuses[service].lastCheck)}</p>
          </div>

          <div>
            <p className="text-sm text-text-secondary">Rate Limit Status:</p>
            <p className="font-medium text-text-primary">
              {rateLimits[service].count}/100 requests per minute
            </p>
            {rateLimits[service].resetTime && (
              <p className="text-sm text-text-secondary">
                Resets at: {formatTime(rateLimits[service].resetTime)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-8 text-text-primary">API Monitor</h1>

      <Tabs defaultValue="location" className="space-y-4">
        <TabsList>
          <TabsTrigger value="location">Location Services</TabsTrigger>
          <TabsTrigger value="email">Email Services</TabsTrigger>
          <TabsTrigger value="phone">Phone Services</TabsTrigger>
        </TabsList>

        <TabsContent value="location">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ServiceCard
              title="Location Verification"
              service="location"
              primaryService="Zippopotam"
              fallbackService="IP-API"
            />
          </div>
        </TabsContent>

        <TabsContent value="email">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ServiceCard
              title="Email Verification"
              service="email"
              primaryService="Abstract API"
              fallbackService="APILayer"
            />
          </div>
        </TabsContent>

        <TabsContent value="phone">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ServiceCard
              title="Phone Verification"
              service="phone"
              primaryService="NumVerify"
              fallbackService="MessageBird"
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="mt-8">
        <button
          onClick={checkApis}
          className="px-4 py-2 bg-button-primary-bg text-button-primary-text rounded hover:bg-button-primary-hover transition-colors"
        >
          Check All Services Now
        </button>
      </div>
    </div>
  );
};

export default ApiMonitor;